import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useChat } from '@/contexts/ChatContext';
import { WebRTCManager } from '@/lib/webrtc/WebRTCManager';
import { useToast } from '@/hooks/use-toast';

export type CallStatus = 'idle' | 'initiating' | 'ringing' | 'connecting' | 'in-call' | 'ended';

export interface CallParticipant {
  userId: string;
  userName: string;
  audio: boolean;
  video: boolean;
}

export interface IncomingCall {
  callId: string;
  callerId: string;
  callerName: string;
  callerAvatar?: string | null;
  callType: 'audio' | 'video';
  roomId?: string;
  isGroupCall: boolean;
}

export interface CallState {
  callId: string | null;
  status: CallStatus;
  callType: 'audio' | 'video';
  isGroupCall: boolean;
  roomId: string | null;
  callerId: string | null;
  participants: CallParticipant[];
  localStream: MediaStream | null;
  remoteStreams: Map<string, MediaStream>;
  isAudioEnabled: boolean;
  isVideoEnabled: boolean;
  callDuration: number;
}

interface CallContextType {
  callState: CallState;
  incomingCall: IncomingCall | null;
  initiateCall: (params: {
    targetUserId?: string;
    targetUserName?: string;
    roomId?: string;
    callType: 'audio' | 'video';
    isGroupCall: boolean;
  }) => Promise<void>;
  acceptCall: () => Promise<void>;
  rejectCall: () => void;
  endCall: () => void;
  toggleAudio: () => void;
  toggleVideo: () => void;
}

const initialCallState: CallState = {
  callId: null,
  status: 'idle',
  callType: 'audio',
  isGroupCall: false,
  roomId: null,
  callerId: null,
  participants: [],
  localStream: null,
  remoteStreams: new Map(),
  isAudioEnabled: true,
  isVideoEnabled: false,
  callDuration: 0,
};

const CallContext = createContext<CallContextType | undefined>(undefined);

export function CallProvider({ children }: { children: React.ReactNode }) {
  const { socket, currentUser, isConnected } = useChat();
  const { toast } = useToast();

  const [callState, setCallState] = useState<CallState>(initialCallState);
  const [incomingCall, setIncomingCall] = useState<IncomingCall | null>(null);

  const callStateRef = useRef<CallState>(callState);
  const incomingCallRef = useRef<IncomingCall | null>(incomingCall);
  const socketRef = useRef(socket);
  const isConnectedRef = useRef(isConnected);

  useEffect(() => { callStateRef.current = callState; }, [callState]);
  useEffect(() => { incomingCallRef.current = incomingCall; }, [incomingCall]);
  useEffect(() => { socketRef.current = socket; }, [socket]);
  useEffect(() => { isConnectedRef.current = isConnected; }, [isConnected]);

  const webrtcRef = useRef<WebRTCManager | null>(null);
  const durationIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const ringtoneCtxRef = useRef<{ ctx: AudioContext; osc1: OscillatorNode; osc2: OscillatorNode; gain: GainNode; interval: ReturnType<typeof setInterval> } | null>(null);

  const getWebRTC = useCallback(() => {
    if (!webrtcRef.current) {
      webrtcRef.current = new WebRTCManager();
    }
    return webrtcRef.current;
  }, []);

  const emitSocket = useCallback((event: string, data?: any) => {
    if (socketRef.current && isConnectedRef.current) {
      socketRef.current.emit(event, data);
    }
  }, []);

  const stopRingtone = useCallback(() => {
    if (ringtoneCtxRef.current) {
      try {
        clearInterval(ringtoneCtxRef.current.interval);
        ringtoneCtxRef.current.osc1.stop();
        ringtoneCtxRef.current.osc2.stop();
        ringtoneCtxRef.current.ctx.close();
      } catch {
      }
      ringtoneCtxRef.current = null;
    }
  }, []);

  const playRingtone = useCallback(async (isOutgoing = false) => {
    stopRingtone();
    try {
      const ctx = new AudioContext();
      if (ctx.state === 'suspended') {
        await ctx.resume().catch(() => {});
      }
      if (ctx.state === 'suspended') {
        ctx.close();
        return;
      }
      const gain = ctx.createGain();
      gain.connect(ctx.destination);
      gain.gain.value = 0;

      const osc1 = ctx.createOscillator();
      const osc2 = ctx.createOscillator();

      if (isOutgoing) {
        osc1.frequency.value = 440;
        osc2.frequency.value = 480;
      } else {
        osc1.frequency.value = 523.25;
        osc2.frequency.value = 659.25;
      }

      osc1.type = 'sine';
      osc2.type = 'sine';

      const osc2Gain = ctx.createGain();
      osc2Gain.gain.value = 0.6;

      osc1.connect(gain);
      osc2.connect(osc2Gain);
      osc2Gain.connect(gain);

      osc1.start();
      osc2.start();

      const onDuration = isOutgoing ? 2000 : 1000;
      const offDuration = isOutgoing ? 4000 : 2000;
      const cycleDuration = onDuration + offDuration;
      const startTime = Date.now();

      const interval = setInterval(() => {
        const elapsed = (Date.now() - startTime) % cycleDuration;
        gain.gain.value = elapsed < onDuration ? 0.3 : 0;
      }, 50);

      gain.gain.value = 0.3;

      ringtoneCtxRef.current = { ctx, osc1, osc2, gain, interval };
    } catch {
    }
  }, [stopRingtone]);

  const startDurationTimer = useCallback(() => {
    if (durationIntervalRef.current) return;
    durationIntervalRef.current = setInterval(() => {
      setCallState(prev => ({ ...prev, callDuration: prev.callDuration + 1 }));
    }, 1000);
  }, []);

  const stopDurationTimer = useCallback(() => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
  }, []);

  const cleanupCall = useCallback((notifyServer = true) => {
    stopRingtone();
    stopDurationTimer();

    if (notifyServer) {
      emitSocket('call:cleanup');
    }

    const currentState = callStateRef.current;
    if (currentState.localStream) {
      currentState.localStream.getTracks().forEach(track => track.stop());
    }

    webrtcRef.current?.closeAllConnections();
    webrtcRef.current = null;
    setCallState(initialCallState);
    setIncomingCall(null);
  }, [stopRingtone, stopDurationTimer, emitSocket]);

  const initiateCall = useCallback(async (params: {
    targetUserId?: string;
    targetUserName?: string;
    roomId?: string;
    callType: 'audio' | 'video';
    isGroupCall: boolean;
  }) => {
    if (callStateRef.current.status !== 'idle') {
      toast({ title: 'Already in a call', variant: 'destructive' });
      return;
    }

    const isVideo = params.callType === 'video';
    const webrtc = getWebRTC();

    try {
      const stream = await webrtc.getLocalStream(true, isVideo);

      setCallState({
        ...initialCallState,
        status: 'initiating',
        callType: params.callType,
        isGroupCall: params.isGroupCall,
        roomId: params.roomId || null,
        callerId: currentUser?.id || null,
        localStream: stream,
        isAudioEnabled: true,
        isVideoEnabled: isVideo,
      });

      emitSocket('call:initiate', {
        targetUserId: params.targetUserId,
        roomId: params.roomId,
        callType: params.callType,
        isGroupCall: params.isGroupCall,
      });
    } catch (err) {
      cleanupCall();
      toast({
        title: 'Cannot start call',
        description: 'Please allow microphone/camera access',
        variant: 'destructive',
      });
    }
  }, [getWebRTC, currentUser?.id, emitSocket, cleanupCall, toast]);

  const acceptCall = useCallback(async () => {
    const incoming = incomingCallRef.current;
    if (!incoming) return;

    const isVideo = incoming.callType === 'video';
    const webrtc = getWebRTC();

    try {
      stopRingtone();
      const stream = await webrtc.getLocalStream(true, isVideo);

      setCallState({
        ...initialCallState,
        callId: incoming.callId,
        status: 'connecting',
        callType: incoming.callType,
        isGroupCall: incoming.isGroupCall,
        roomId: incoming.roomId || null,
        callerId: incoming.callerId,
        localStream: stream,
        isAudioEnabled: true,
        isVideoEnabled: isVideo,
      });
      setIncomingCall(null);

      emitSocket('call:accept', {
        callId: incoming.callId,
        userName: currentUser?.full_name || currentUser?.email || 'Unknown',
      });
    } catch (err) {
      cleanupCall();
      toast({
        title: 'Cannot join call',
        description: 'Please allow microphone/camera access',
        variant: 'destructive',
      });
    }
  }, [getWebRTC, stopRingtone, currentUser, emitSocket, cleanupCall, toast]);

  const rejectCall = useCallback(() => {
    const incoming = incomingCallRef.current;
    if (!incoming) return;
    stopRingtone();
    emitSocket('call:reject', { callId: incoming.callId });
    setIncomingCall(null);
  }, [stopRingtone, emitSocket]);

  const endCall = useCallback(() => {
    const current = callStateRef.current;

    if (current.callId) {
      if (current.status === 'ringing' || current.status === 'initiating') {
        emitSocket('call:cancel', { callId: current.callId });
      } else {
        emitSocket('call:end', { callId: current.callId });
      }
    }

    cleanupCall();
  }, [emitSocket, cleanupCall]);

  const toggleAudio = useCallback(() => {
    const current = callStateRef.current;
    const newEnabled = !current.isAudioEnabled;
    webrtcRef.current?.toggleAudio(newEnabled);
    setCallState(prev => ({ ...prev, isAudioEnabled: newEnabled }));
    if (current.callId) {
      emitSocket('call:toggle-media', {
        callId: current.callId,
        audio: newEnabled,
        video: current.isVideoEnabled,
      });
    }
  }, [emitSocket]);

  const toggleVideo = useCallback(() => {
    const current = callStateRef.current;
    const newEnabled = !current.isVideoEnabled;
    webrtcRef.current?.toggleVideo(newEnabled);
    setCallState(prev => ({ ...prev, isVideoEnabled: newEnabled }));
    if (current.callId) {
      emitSocket('call:toggle-media', {
        callId: current.callId,
        audio: current.isAudioEnabled,
        video: newEnabled,
      });
    }
  }, [emitSocket]);

  useEffect(() => {
    if (!socket || !isConnected) return;

    const onCallInitiated = (data: { callId: string }) => {
      setCallState(prev => {
        if (prev.status !== 'initiating') return prev;
        return { ...prev, callId: data.callId, status: 'ringing' };
      });
      playRingtone(true);
    };

    const onCallIncoming = (data: IncomingCall) => {
      const currentStatus = callStateRef.current.status;
      const currentCallId = callStateRef.current.callId;

      if (currentCallId === data.callId) return;

      if (incomingCallRef.current?.callId === data.callId) return;

      if (currentStatus !== 'idle') {
        emitSocket('call:reject', { callId: data.callId });
        return;
      }

      setIncomingCall(data);
      playRingtone(false);
    };

    const onCallAccepted = async (data: { callId: string; acceptedBy: string }) => {
      stopRingtone();
      setCallState(prev => ({ ...prev, status: 'connecting' }));

      const webrtc = getWebRTC();

      webrtc.onIceCandidate = (targetUserId, candidate) => {
        emitSocket('call:ice-candidate', {
          callId: data.callId,
          targetUserId,
          candidate,
        });
      };
      webrtc.onRemoteStream = (userId, stream) => {
        setCallState(prev => {
          const newStreams = new Map(prev.remoteStreams);
          newStreams.set(userId, stream);
          return { ...prev, remoteStreams: newStreams, status: 'in-call' };
        });
        startDurationTimer();
      };
      webrtc.onRemoteStreamRemoved = (userId) => {
        setCallState(prev => {
          const newStreams = new Map(prev.remoteStreams);
          newStreams.delete(userId);
          return { ...prev, remoteStreams: newStreams };
        });
      };

      try {
        const offer = await webrtc.createOffer(data.acceptedBy);
        emitSocket('call:offer', {
          callId: data.callId,
          targetUserId: data.acceptedBy,
          sdp: offer,
        });
      } catch (err) {
        console.error('Failed to create WebRTC offer:', err);
        toast({ title: 'Call connection failed', variant: 'destructive' });
        cleanupCall();
      }
    };

    const onCallOffer = async (data: { callId: string; fromUserId: string; sdp: RTCSessionDescriptionInit }) => {
      const webrtc = getWebRTC();

      if (!webrtc.onIceCandidate) {
        webrtc.onIceCandidate = (targetUserId, candidate) => {
          emitSocket('call:ice-candidate', {
            callId: data.callId,
            targetUserId,
            candidate,
          });
        };
        webrtc.onRemoteStream = (userId, stream) => {
          setCallState(prev => {
            const newStreams = new Map(prev.remoteStreams);
            newStreams.set(userId, stream);
            return { ...prev, remoteStreams: newStreams, status: 'in-call' };
          });
          startDurationTimer();
        };
        webrtc.onRemoteStreamRemoved = (userId) => {
          setCallState(prev => {
            const newStreams = new Map(prev.remoteStreams);
            newStreams.delete(userId);
            return { ...prev, remoteStreams: newStreams };
          });
        };
      }

      try {
        const answer = await webrtc.handleOffer(data.fromUserId, data.sdp);
        emitSocket('call:answer', {
          callId: data.callId,
          targetUserId: data.fromUserId,
          sdp: answer,
        });
      } catch (err) {
        console.error('Failed to handle WebRTC offer:', err);
      }
    };

    const onCallAnswer = async (data: { callId: string; fromUserId: string; sdp: RTCSessionDescriptionInit }) => {
      try {
        await getWebRTC().handleAnswer(data.fromUserId, data.sdp);
      } catch (err) {
        console.error('Failed to handle WebRTC answer:', err);
      }
    };

    const onIceCandidate = async (data: { callId: string; fromUserId: string; candidate: RTCIceCandidateInit }) => {
      try {
        await getWebRTC().addIceCandidate(data.fromUserId, data.candidate);
      } catch (err) {
        console.error('Failed to add ICE candidate:', err);
      }
    };

    const onCallRejected = () => {
      toast({ title: 'Call rejected' });
      cleanupCall(false); // Server already cleaned up
    };

    const onCallEnded = (data: { callId: string; endedBy: string; reason: string; duration: number }) => {
      if (data.reason === 'no_answer') {
        toast({ title: 'No answer' });
      }
      cleanupCall(false); // Server already cleaned up
    };

    const onCallCancelled = () => {
      toast({ variant: "success", title: 'Call cancelled' });
      cleanupCall(false); // Server already cleaned up
    };

    const onCallMissed = (data: { callId: string; callerId: string; callerName: string }) => {
      toast({ title: `Missed call from ${data.callerName}` });
      cleanupCall(false); // Server already cleaned up
    };

    const onCallBusy = () => {
      toast({ title: 'User is busy' });
      cleanupCall(false);
    };

    const onCallError = (data: { message: string }) => {
      toast({ title: 'Call error', description: data.message, variant: 'destructive' });
      cleanupCall(true);
    };

    const onParticipantJoined = async (data: { callId: string; userId: string; userName: string }) => {
      setCallState(prev => ({
        ...prev,
        participants: [...prev.participants.filter(p => p.userId !== data.userId), {
          userId: data.userId,
          userName: data.userName,
          audio: true,
          video: prev.callType === 'video',
        }],
      }));

      const webrtc = getWebRTC();
      try {
        const offer = await webrtc.createOffer(data.userId);
        emitSocket('call:offer', {
          callId: data.callId,
          targetUserId: data.userId,
          sdp: offer,
        });
      } catch (err) {
        console.error(`Failed to create offer for new participant ${data.userId}:`, err);
      }
    };

    const onParticipantLeft = (data: { callId: string; userId: string }) => {
      webrtcRef.current?.removePeer(data.userId);
      setCallState(prev => {
        const newParticipants = prev.participants.filter(p => p.userId !== data.userId);
        const newStreams = new Map(prev.remoteStreams);
        newStreams.delete(data.userId);

        if (newStreams.size === 0 && prev.status === 'in-call') {
          setTimeout(() => {
            toast({ title: 'Everyone else left the call' });
            cleanupCall(true);
          }, 0);
        }

        return { ...prev, participants: newParticipants, remoteStreams: newStreams };
      });
    };

    const onMediaState = (data: { callId: string; userId: string; audio: boolean; video: boolean }) => {
      setCallState(prev => ({
        ...prev,
        participants: prev.participants.map(p =>
          p.userId === data.userId ? { ...p, audio: data.audio, video: data.video } : p
        ),
      }));
    };

    if (callStateRef.current.status === 'idle') {
      socket.emit('call:cleanup');
    }

    socket.on('call:initiated', onCallInitiated);
    socket.on('call:incoming', onCallIncoming);
    socket.on('call:accepted', onCallAccepted);
    socket.on('call:offer', onCallOffer);
    socket.on('call:answer', onCallAnswer);
    socket.on('call:ice-candidate', onIceCandidate);
    socket.on('call:rejected', onCallRejected);
    socket.on('call:ended', onCallEnded);
    socket.on('call:cancelled', onCallCancelled);
    socket.on('call:missed', onCallMissed);
    socket.on('call:busy', onCallBusy);
    socket.on('call:error', onCallError);
    socket.on('call:participant-joined', onParticipantJoined);
    socket.on('call:participant-left', onParticipantLeft);
    socket.on('call:media-state', onMediaState);

    return () => {
      socket.off('call:initiated', onCallInitiated);
      socket.off('call:incoming', onCallIncoming);
      socket.off('call:accepted', onCallAccepted);
      socket.off('call:offer', onCallOffer);
      socket.off('call:answer', onCallAnswer);
      socket.off('call:ice-candidate', onIceCandidate);
      socket.off('call:rejected', onCallRejected);
      socket.off('call:ended', onCallEnded);
      socket.off('call:cancelled', onCallCancelled);
      socket.off('call:missed', onCallMissed);
      socket.off('call:busy', onCallBusy);
      socket.off('call:error', onCallError);
      socket.off('call:participant-joined', onParticipantJoined);
      socket.off('call:participant-left', onParticipantLeft);
      socket.off('call:media-state', onMediaState);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, isConnected]);

  useEffect(() => {
    return () => {
      stopRingtone();
      stopDurationTimer();
      webrtcRef.current?.closeAllConnections();
      webrtcRef.current = null;
    };
  }, [stopRingtone, stopDurationTimer]);

  const value: CallContextType = {
    callState,
    incomingCall,
    initiateCall,
    acceptCall,
    rejectCall,
    endCall,
    toggleAudio,
    toggleVideo,
  };

  return (
    <CallContext.Provider value={value}>
      {children}
    </CallContext.Provider>
  );
}

export function useCall() {
  const ctx = useContext(CallContext);
  if (!ctx) {
    throw new Error('useCall must be used within a CallProvider');
  }
  return ctx;
}
